import React from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { privateRoutes } from "router/routes"
import * as BikeApi from "features/bike/api/BikeApi"
import * as ConsumerApi from "features/bike/api/ConsumerApi"

import { useAlert, useAuth, useModal, useBikeStore } from "hooks"

import FormElement from "components/Form/FormElement"
import { normalizeBoolean } from "utils/functions"
import { getEntityId, getOldStoreId } from "utils/user"
import { ModalScanCode } from "components/Elements/ScanOcode"
import { ScanIcon } from "components/Elements/Icons"
import { userEntityGenderList, BIKE_BACK_STORE, INTER_CHAIN, MFC_OPERATOR, MFC_RETAIL_GENDER, VELOCISTE_GENDER, ARCADE_STOREID, ARCADE_STOREID_ST } from "utils/constants"

const SHORT_CODE_LENGTH = 6
const defaultFormFields = {
    consumerId: "",
    ownerGender: userEntityGenderList[0].value,
    isWallet: false,
    ownerName: "",
    ownerFirstname: "",
    ownerRaisonSociale: "",
    ownerPhonenumber: "",
    ownerMail: "",
    ownerConfirmNewMail: "",
    ownerCode: ""
}

const defaultFormEvents = {
    ready: true
}

export const FormSellBike = () => {
    const {
        currentBike: bike,
        setInitialBike,
    } = useBikeStore()

    const [formFields, setFormFields] = React.useState(bike)
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
    const [resetPhone, setResetPhone] = React.useState(false)

    const [errors, setErrors] = React.useState([])
    const [showLocalErrors, setShowLocalErrors] = React.useState(false)
    const [apiError, setApiError] = React.useState(null)

    const { alert } = useAlert()
    const { showModal } = useModal()
    const { t } = useTranslation()
    const { entity, logout } = useAuth()
	const navigate = useNavigate()

    const isManufacturerNFT = bike.descriptionMadeBy === "MANUFACTURER_NFT"
    const isVelociste = entity.gender === VELOCISTE_GENDER

    const handleInputChange = (event) => {
        const target = event.target
        const value = target.type === "checkbox" ? target.checked : target.value
        const name = target.name
        setFormFields({
            ...formFields,
            [name]: value
        })
    }

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
    }, [])

    const handleSubmit = async (event) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)

        if (!formReady) {
            setShowLocalErrors(true)
        }
        else {
            setApiError(null)
            // Open confirmation modal
            showModal(
                ModalConfirmation,
                {
                    body: () => (
                        <div className="lg:px-12">
                            <h2 className="c-h2 text-primary font-bold text-center mt-7 mb-12">
                                {t("sell_modal_confirm_title", { ns: "bike" })}
                            </h2>
                            <p className="mb-2">{t("sell_modal_confirm_description", { ns: "bike" })}</p>
                            <div className="l-grid u-mg-negative-hz-m mb-12">
                                <div className="lg:w-1/2 w-full p-5">
                                    <p className="">{t("sell_modal_confirm_bikeCode", { ns: "bike" })}</p>
                                    <p className="text-primary font-bold lg:text-m">{bike?.code}</p>
                                </div>
                                <div className="lg:w-1/2 w-full p-5">
                                    <p className="">{t("sell_modal_confirm_ownerMail", { ns: "bike" })}</p>
                                    <p className="text-primary font-bold lg:text-m">{formFields?.ownerMail}</p>
                                </div>
                            </div>
                        </div>
                    ),
                    callback: async () => {
                        // hide error
                        setShowLocalErrors(false)
                        // prevent form to be submitted again
                        setFormEvents({
                            ...formEvents,
                            ready: false
                        })

                        // to be able to register it, you must ensure that the store is authorized by the operator
                        const isAble = entity?.entity_data?.entity_permission?.operateurs.find(oneOperator => oneOperator === bike?.operator)
                        if (typeof isAble === "undefined") {
                            alert("error", t("sell_error_NotAuthorizedException", { ns: "api" }))
                            setFormEvents({
                                ...formEvents,
                                ready: true
                            })
                            return
                        }

                        // if status 6 (retour magasin), on contrôle que ce magasin est autorisé
                        if (bike.status === BIKE_BACK_STORE) {
                            const bikeStoreId = bike.storeId
                            const storeStoreId = "ST-" + getEntityId()
                            const oldStoreId = getOldStoreId()

                            // Un vendeur MFC avec un gender Intersport doit pouvoir revendre un vélo MFC status 6 d'un autre vélociste
                            // Attention, ici on fait aussi un comparatif avec le store id Arcade
                            const isMFCIntersportSeller = entity?.entity_data?.code_chaine === INTER_CHAIN
                            const isMFCBike = bike?.operator === MFC_OPERATOR

                            if (bikeStoreId !== storeStoreId && bikeStoreId !== oldStoreId && bikeStoreId !== ARCADE_STOREID && bikeStoreId !== ARCADE_STOREID_ST && !(isMFCIntersportSeller && isMFCBike)) {
                                alert("error", t("sell_error_NotAuthorizedException", { ns: "api" }))
                                setFormEvents({
                                    ...formEvents,
                                    ready: true
                                })
                                return
                            }
                        }

                        // 1. Create bike_description
                        // --------
                        const bike_description = {
                            gear_type_id: bike.bikeTypeId, // @TODO
                            gear_type: bike.bikeType,
                            brand_id: bike.bikeBrandId,
                            brand: bike.bikeBrand,
                            model: bike.bikeModel,
                            color: bike.bikeColor,
                            is_electric: normalizeBoolean(bike.isVAE)
                        }
                        if (bike.bikeSerial) {
                            bike_description.frame_serial_number = bike.bikeSerial
                        }
                        if (normalizeBoolean(bike.isVAE) && bike.bikeEngineSerial) {
                            bike_description.engine_serial_number = bike.bikeEngineSerial
                        }
                        if (normalizeBoolean(bike.isVAE) && bike.bikeBatterySerial) {
                            bike_description.battery_serial_number = bike.bikeBatterySerial
                        }

                        // 2. Create bike_owner
                        // --------
                        const bike_owner = {}
                        if (formFields.isWallet) {
                            bike_owner.consumer_id = formFields.consumerId
                        }
                        else {
                            if (parseInt(formFields.ownerGender) === 1) { // particulier
                              bike_owner.first_name = formFields.ownerFirstname
                              bike_owner.last_name = formFields.ownerName
                            }
                            else {
                              bike_owner.social_reason = formFields.ownerRaisonSociale
                            }
                            bike_owner.gender = parseInt(formFields.ownerGender)
                            bike_owner.phone = formFields.ownerPhonenumber
                            bike_owner.mail = formFields.ownerMail.trim()
                        }

                        // 3. Create the new bike to be sent
                        // ------------
                        let template = "default"
                        if (bike?.operator === MFC_OPERATOR) {
                            const operator_detail = entity?.data_operators.find(oneOperator => oneOperator.operator === bike?.operator)
                            const operator_gender = operator_detail?.entity_data?.operator_gender
                            if (operator_gender !== MFC_RETAIL_GENDER) {
                                template = operator_detail?.entity_data?.operator_gender
                            }

                        }

                        const newBike = {
                            bike_id: bike.code,
                            bike_status: 1,
                            bike_description: bike_description,
                            bike_owner: bike_owner,
                            send_notification_by_mail: template, //sunn
                            send_receipt_by_mail: template, //sunn
                            send_pro_sale_event: isManufacturerNFT
                        }

                        try {
                            const { getError } = await BikeApi.createBike(newBike)
                            if (getError()) throw getError()

                            // We need bike operator for set a logo in the PDF
                            newBike.operator = bike.operator
                            alert("success", t("sell_success", { ns: "bike", bikeCode: formFields?.bikeCode }))

                            // Updating bike info. Here, we need all information about bike owner for the PDF
                            // If we fetch bike, firstname, lastname and phone are no longer accessible
                            // Then, redirect to BikeUpdated
                            setInitialBike(newBike)
                            navigate({
                                pathname: `${privateRoutes.updatedBike.basePath}/${bike?.code}`,
                            }, { variant: "sell-succcess" })

                        } catch (error) {
                            console.error(error)
                            const erroMsg = error?.message?.toString()
                            if (erroMsg) alert("error", erroMsg)
                        } finally {
                            setFormEvents(s => ({
                                ...s,
                                ready: true
                            }))
                        }
                    }
                },
                { style: { maxWidth: "900px" } }
            )
        }
    }

    // Manage form autocomplete with an O°Code
    // --------------------------
    // When a code is scanned, fill the form
    const scanCode = () => {
        showModal(
            ModalScanCode,
            {
                title: t("scan_code_title", { ns: "bike" }),
                callback: async ({ ocode }) => {
                    try {
                        // console.log("ocode: ", ocode)
                        setApiError(null)
                        const { res: consumer, getError } = await ConsumerApi.getConsumer(ocode, "byOcodeId")
                        if (getError()) throw getError()
                        setFormFields(s => ({
                            ...s,
                            ownerGender: consumer?.gender,
                            isWallet: consumer?.consumer_id !== null,
                            consumerId: consumer?.consumer_id,
                            ownerName: consumer?.last_name || "",
                            ownerFirstname: consumer?.first_name || "",
                            ownerPhonenumber: consumer?.phone,
                            ownerMail: consumer?.mail,
                            ownerConfirmNewMail: consumer?.mail,
                            ownerRaisonSociale: consumer?.social_reason || ""
                        }))
                    } catch (error) {
                        console.error(error)
                        const erroMsg = error?.message?.toString()
                        if (erroMsg) alert("error", erroMsg)

                        if (["ConsumerNotFoundException", "ExpiredShortCodeIdException", "MoonCodeNotFoundException", "InvalidArgument"].includes(error?.code)) setApiError(erroMsg)
                        if (["Access token is required."].includes(error?.error?.message)) logout()
                        // reset
                        setFormFields(defaultFormFields)
                    }

                }
            }
        )
    }
    // When a code is manually entered, fill the form
    React.useEffect(() => {
        ; (async () => {
            try {
                setResetPhone(false)
                if (formFields?.ownerCode?.length === SHORT_CODE_LENGTH) {
                    setApiError(null)
                    const { res: consumer, getError } = await ConsumerApi.getConsumer(formFields.ownerCode, "byShortCodeId")
                    if (getError()) throw getError()
                    setFormFields(s => ({
                        ...s,
                        ownerGender: consumer?.gender,
                        isWallet: consumer?.consumer_id !== null,
                        consumerId: consumer?.consumer_id,
                        ownerName: consumer?.last_name || "",
                        ownerFirstname: consumer?.first_name || "",
                        ownerPhonenumber: consumer?.phone,
                        ownerMail: consumer?.mail,
                        ownerConfirmNewMail: consumer?.mail,
                        ownerRaisonSociale: consumer?.social_reason || ""
                    }))
                }
            } catch (error) {
                console.error(error)
                const erroMsg = error?.message?.toString()
                if (erroMsg) alert("error", erroMsg)

                if (["ConsumerNotFoundException", "ExpiredShortCodeIdException", "MoonCodeNotFoundException", "InvalidArgument"].includes(error?.code)) setApiError(erroMsg)
                if (["Access token is required."].includes(error?.error?.message)) logout()

                // reset fields
                setResetPhone(true)
                setFormFields(defaultFormFields)
            }


        })()
    }, [formFields.ownerCode]) //eslint-disable-line

    return (
        <form className="">

            <section className="mb-5">
                <h2 className="c-h2 font-semibold mb-5">{t("sell_update_owner_title", { ns: "bike" })}</h2>
                <div className="l-grid flex-col md:flex-row-reverse u-mg-negative-hz-m">
                    <div className="lg:w-1/2 w-full p-5">
                        <h3 className="c-h4 uppercase mb-7">{t("sell_update_owner_title_automatically", { ns: "bike" })}</h3>
                        {/* ownerCode */}
                        <div className="flex items-start mr-7 mb-7">
                            <FormElement
                                value={formFields.ownerCode}
                                name="ownerCode"
                                type="text"
                                label={t("owner.code", { ns: "bike" })}
                                className=""
                                style={{ minWidth: "calc(100% - 20px)" }}
                                required={false}
                                showErrors={showLocalErrors}
                                onValidate={handleValidation}
                                onChange={handleInputChange}
                            />
                            <button
                                type="button"
                                className="c-btn-form-group"
                                onClick={scanCode}
                            >
                                <ScanIcon size={40} />
                            </button>
                        </div>
                        <p className="text-s mb-7">{t("owner.codeInformations", { ns: "bike" })}</p>
                    </div>
                    <div className="lg:w-1/2 w-full p-5">
                        <h3 className="c-h4 uppercase mb-7">{t("sell_update_owner_title_manually", { ns: "bike" })}</h3>
                        {/* VAE */}
                        <FormElement
                            value={formFields.ownerGender}
                            name="ownerGender"
                            type="radio"
                            options={userEntityGenderList}
                            className="mb-7"
                            classNameOptionsContainer="flex-row w-full"
                            classNameLabel="mb-5"
                            classNameOption="flex-1 items-center"
                            required={true}
                            showErrors={showLocalErrors}
                            onValidate={handleValidation}
                            onChange={handleInputChange}
                        />
                        {parseInt(formFields.ownerGender) === 1 && // private
                            <>
                                {/* ownerName */}
                                <FormElement
                                    value={formFields.ownerName}
                                    name="ownerName"
                                    type="text"
                                    label={t("owner.name", { ns: "bike" })}
                                    className="mb-7"
                                    required={true}
                                    showErrors={showLocalErrors}
                                    onValidate={handleValidation}
                                    onChange={handleInputChange}
                                />
                                {/* ownerFirstname */}
                                <FormElement
                                    value={formFields.ownerFirstname}
                                    name="ownerFirstname"
                                    type="text"
                                    label={t("owner.firstname", { ns: "bike" })}
                                    className="mb-7"
                                    required={true}
                                    showErrors={showLocalErrors}
                                    onValidate={handleValidation}
                                    onChange={handleInputChange}
                                />
                            </>
                        }
                        {parseInt(formFields.ownerGender) === 2 && // company
                            <>
                                {/* ownerRaisonSociale */}
                                <FormElement
                                    value={formFields.ownerRaisonSociale}
                                    name="ownerRaisonSociale"
                                    type="text"
                                    label={t("owner.raison_sociale", { ns: "bike" })}
                                    className="mb-7"
                                    required={true}
                                    showErrors={showLocalErrors}
                                    onValidate={handleValidation}
                                    onChange={handleInputChange}
                                />
                            </>
                        }

                        {/* ownerPhonenumber */}
                        <FormElement
                            value={formFields.ownerPhonenumber}
                            name="ownerPhonenumber"
                            type="phone"
                            label={t("owner.phonenumber", { ns: "bike" })}
                            className="mb-7"
                            required={true}
                            isResetField={resetPhone}
                            showErrors={showLocalErrors}
                            onValidate={handleValidation}
                            onChange={handleInputChange}
                        />

                        {/* ownerMail */}
                        <FormElement
                            value={formFields.ownerMail}
                            name="ownerMail"
                            type="email"
                            label={t("owner.mail", { ns: "bike" })}
                            className="mb-7"
                            required={true}
                            showErrors={showLocalErrors}
                            onValidate={handleValidation}
                            onChange={handleInputChange}
                        />

                        {/* ownerConfirmNewMail */}
                        {/* Not required for asso */}
                        {isVelociste && <FormElement
                            value={formFields.ownerConfirmNewMail}
                            name="ownerConfirmNewMail"
                            type="email"
                            label={t("owner.newMailConfirmation", { ns: "bike" })}
                            mailToConfirm={formFields.ownerMail}
                            className="mb-5"
                            required={true}
                            showErrors={showLocalErrors}
                            onValidate={handleValidation}
                            onChange={handleInputChange}
                        />}
                    </div>
                </div>
            </section>
            {apiError &&
                <div className="mb-7">
                    <p className="text-danger">{apiError}</p>

                </div>
            }
            <div className="flex justify-end mt-7 mb-5">
                {formEvents.ready &&
                    <button
                        type="submit"
                        className="c-btn c-btn--primary px-12"
                        onClick={handleSubmit}
                        // disabled={errors.length > 0}
                    >
                        {t("sell", { ns: "bike" })}
                    </button>
                }
                {!formEvents.ready &&
                    <div className="c-spinner"></div>
                }
            </div>

        </form>
    )
}

const ModalConfirmation = ({ body, callback }) => {
    const { hideModal } = useModal()
    const { t } = useTranslation()
    const validate = () => {
        hideModal()
        callback()
    }
    return (
        <div className="p-12">
            {React.createElement(body)}
            <div className="flex justify-between flex-col md:flex-row-reverse">
                <button
                    type="button"
                    className="c-btn c-btn--primary px-12 mb-5 lg:m-0"
                    onClick={validate}
                >
                    {t("global.validate")}
                </button>
                <button
                    type="button"
                    className="c-btn c-btn--white-bordered px-7 mb-5 lg:m-0"
                    onClick={hideModal}
                >
                    {t("global.cancel")}
                </button>
            </div>
        </div>
    )
}
